import { createFileRoute, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';

import { GamePlayer } from '~/components/game/GamePlayer';
import { useHeader } from '~/contexts/HeaderContext';
import { readFinalScore } from '~/lib/finalScore';
import type { PublicLandingPage } from '~/types';

export const Route = createFileRoute('/$slug/game/$episodeId')({
  component: GamePage,
});

function GamePage() {
  const router = useRouter();
  const { slug, landingPage } = Route.useRouteContext() as {
    slug: string;
    landingPage: PublicLandingPage;
  };
  const { episodeId } = Route.useParams();
  const { setHideHeader } = useHeader();

  // Hide header immediately on game page and restore when leaving
  useEffect(() => {
    setHideHeader(true);
    return () => setHideHeader(false);
  }, [setHideHeader]);

  // The score page reads finalized totals from sessionStorage (written by
  // GamePlayer once the server finalize call returns). The navigation here
  // carries no score data on purpose — URL search params are not a source
  // of truth in the no-accounts flow.
  const handleGameComplete = useCallback(
    (scoreData?: { score: number; totalQuestions: number }) => {
      setHideHeader(false);

      if (scoreData) {
        router.navigate({ to: '/$slug/score', params: { slug } });
      } else {
        // Navigate to the parameterized offer route so the server can resolve
        // any per-episode offer override. sessionRef comes from the finalized
        // game session written by GamePlayer to sessionStorage.
        const final = readFinalScore(slug);
        const sessionRef = final?.sessionRef;
        router.navigate({
          to: '/$slug/offer/$episodeId',
          params: { slug, episodeId },
          search: sessionRef ? { sessionRef } : {},
        });
      }
    },
    [router, slug, episodeId, setHideHeader],
  );

  const handlePlayStart = useCallback(() => {
    setHideHeader(true);
  }, [setHideHeader]);

  return (
    <div className="h-[100dvh] w-screen overflow-hidden">
      <GamePlayer
        slug={slug}
        episodeId={episodeId}
        gameOptions={landingPage.gameOptions}
        onGameComplete={handleGameComplete}
        onPlayStart={handlePlayStart}
        className="h-full w-full"
      />
    </div>
  );
}
